// Stolen from https://stackoverflow.com/posts/52171480/revisions
export function hash(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for(let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1  = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2  = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

export function getUserNameInitials(user?: { first_name?: string; last_name?: string } | null) {
  if (!user) {
    return "";
  }
  return (user.first_name?.charAt(0) ?? "") + (user.last_name?.charAt(0) ?? "");
}

export function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + "...";
}

/** True when the value is a string that can be rendered as an external link. */
export function isHttpsLink(value: unknown): boolean {
  return typeof value === "string" && value.trim().startsWith("https://");
}

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Tags whose text content carries no value and must be dropped entirely.
const SANITIZE_DROP_TAGS = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META",
  "BASE", "FORM", "SVG", "MATH", "TEMPLATE", "NOSCRIPT",
]);

// Tags we allow to render. Anything else is unwrapped (its text is kept, the tag is removed).
const SANITIZE_ALLOWED_TAGS = new Set([
  "A", "B", "STRONG", "I", "EM", "U", "S", "SUB", "SUP", "BR", "SPAN", "P",
  "UL", "OL", "LI", "DIV", "IMG", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "SMALL", "CODE", "PRE",
  "TABLE", "THEAD", "TBODY", "TR", "TD", "TH",
]);

// Per-tag allowed attributes on top of the global ones.
const SANITIZE_ALLOWED_ATTRS: Record<string, Set<string>> = {
  A: new Set(["href", "target", "rel"]),
  IMG: new Set(["src", "alt", "title", "width", "height"]),
};
const SANITIZE_GLOBAL_ATTRS = new Set(["class", "title"]);

const SANITIZE_SAFE_HREF = /^(?:https:|mailto:|tel:|#|\/(?!\/)|\.{1,2}\/)/i;
const SANITIZE_SAFE_IMG_SRC = /^(?:https:|\/(?!\/)|\.{1,2}\/|data:image\/(?:png|gif|jpe?g|webp))/i;

function sanitizeElement(el: Element): void {
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const tag = el.tagName.toUpperCase();
    const isAllowed =
      SANITIZE_GLOBAL_ATTRS.has(name) || SANITIZE_ALLOWED_ATTRS[tag]?.has(name);

    if (!isAllowed) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === "href" && !SANITIZE_SAFE_HREF.test(attr.value.trim())) {
      el.removeAttribute(attr.name);
    }
    if (name === "src" && !SANITIZE_SAFE_IMG_SRC.test(attr.value.trim())) {
      el.removeAttribute(attr.name);
    }
  }

  if (el.tagName.toUpperCase() === "A" && el.getAttribute("target") === "_blank") {
    // Prevent reverse-tabnabbing on links opened in a new tab.
    const existing = (el.getAttribute("rel") ?? "").split(/\s+/).filter(Boolean);
    const rel = new Set(existing);
    rel.add("noopener");
    rel.add("noreferrer");
    el.setAttribute("rel", Array.from(rel).join(" "));
  }
}

function sanitizeChildren(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toUpperCase();

      if (SANITIZE_DROP_TAGS.has(tag)) {
        el.remove();
        continue;
      }

      sanitizeChildren(el);

      if (!SANITIZE_ALLOWED_TAGS.has(tag)) {
        // Unwrap unknown tags: keep their (already sanitized) children as plain content.
        el.replaceWith(...Array.from(el.childNodes));
        continue;
      }

      sanitizeElement(el);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      // Comments can carry conditional-comment payloads; drop them.
      child.parentNode?.removeChild(child);
    }
  }
}

/**
 * Allow-list HTML sanitizer for values that are rendered via v-html.
 *
 * Keeps a small set of formatting tags (so e.g. unit markup like m<sup>2</sup> still renders)
 * while stripping scripts, event handlers, javascript: URLs, and any other unknown markup.
 * Use this instead of binding untrusted server/user strings to v-html directly.
 */
export function sanitizeHtml(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const html = String(value);
  if (html === "") {
    return "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  sanitizeChildren(doc.body);
  return doc.body.innerHTML;
}

function normalizeForFuzzyMatch(str: string): string {
  return str.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

/**
 * Sørensen–Dice similarity over normalized whitespace-separated tokens.
 * Returns a value in [0, 1] where 1 means identical token sets and 0 means no shared tokens.
 * Non-alphanumeric characters are treated as separators and casing is ignored.
 */
export function fuzzyMatchScore(a: string, b: string): number {
  const tokensA = new Set(normalizeForFuzzyMatch(a).split(' ').filter(Boolean));
  const tokensB = new Set(normalizeForFuzzyMatch(b).split(' ').filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let common = 0;
  tokensA.forEach(token => { if (tokensB.has(token)) common++; });
  return (2 * common) / (tokensA.size + tokensB.size);
}

/**
 * Find the option whose name is the closest fuzzy match to any of the given queries.
 * Each option is scored against every (truthy) query and the highest of those scores is used.
 * Options scoring below `minSimilarity` are ignored. Ties are broken alphabetically by name.
 * Returns null when no option clears the threshold.
 */
export function findClosestFuzzyMatch<T>(
  options: T[],
  getName: (option: T) => string,
  queries: (string | null | undefined)[],
  minSimilarity = 0.4,
): T | null {
  let best: { option: T; score: number; name: string } | null = null;
  for (const option of options) {
    const name = getName(option);
    let score = 0;
    for (const query of queries) {
      if (!query) continue;
      const s = fuzzyMatchScore(query, name);
      if (s > score) score = s;
    }
    if (score < minSimilarity) continue;
    if (
      !best
      || score > best.score
      || (score === best.score && name.toLowerCase() < best.name.toLowerCase())
    ) {
      best = { option, score, name };
    }
  }
  return best?.option ?? null;
}
